import type { AgentConfig, MenuResponse } from '../types'

/**
 * The path the voice agent answers on, under our own origin. It has no CORS
 * middleware, so same-origin is the only way these are reachable at all.
 *
 * Two deployments, which is why this is a build-time value:
 *   `/agent`  the dashboard host, where nginx proxies the prefix to the agent
 *   `/`       agent.quickcomm.ae, where the agent already owns /ws, /config and
 *             /menu at the root and the page is served beside it
 *
 * Root is spelt `/` rather than left empty on purpose: an unset Docker ARG
 * reaches Vite as an empty string, so treating empty as "root" would silently
 * point the dashboard build at paths it does not serve.
 */
const CONFIGURED = import.meta.env.VITE_AGENT_BASE || '/agent'
export const AGENT_BASE: string = CONFIGURED.replace(/\/+$/, '')

/**
 * What the page runs on if `/config` cannot be reached. The agent's own
 * defaults, copied: ops flips these without a frontend redeploy, so a failed
 * fetch degrades to sane values rather than blocking a customer.
 */
export const CONFIG_FALLBACK: AgentConfig = {
  phone_ui: 'full',
  sample_rate: 16000,
  tts_tail_ms: 300,
  playback_lead_ms: 150,
}

// Plain fetch, deliberately NOT the shared axios instance. That one injects a
// Bearer token from sessionStorage and turns a 401 into a hard redirect to
// /login — which on a page a stranger opened from a sticker would throw them
// into a dashboard sign-in screen. This page has no session and wants none.
async function getJson<T>(path: string): Promise<T> {
  const res = await fetch(`${AGENT_BASE}${path}`)
  if (!res.ok) throw new Error(`${path} responded ${res.status}`)
  return (await res.json()) as T
}

export const agentService = {
  /** Never rejects. A page that cannot read its config still has to open. */
  async config(): Promise<AgentConfig> {
    try {
      const raw = await getJson<Partial<AgentConfig>>('/config')
      return {
        // Anything the agent does not send, or sends unrecognisably, falls back
        // one field at a time rather than discarding the whole response.
        phone_ui: raw.phone_ui === 'sphere' ? 'sphere' : 'full',
        sample_rate: raw.sample_rate ?? CONFIG_FALLBACK.sample_rate,
        tts_tail_ms: raw.tts_tail_ms ?? CONFIG_FALLBACK.tts_tail_ms,
        playback_lead_ms: raw.playback_lead_ms ?? CONFIG_FALLBACK.playback_lead_ms,
      }
    } catch {
      return CONFIG_FALLBACK
    }
  },

  /**
   * The menu the agent itself is working from, which is the point of asking the
   * agent rather than the backend — the page must never show a menu the agent
   * does not have. An unknown property answers 502, not an empty menu.
   */
  async menu(propertyId: string): Promise<MenuResponse> {
    return getJson<MenuResponse>(`/menu?property_id=${encodeURIComponent(propertyId)}`)
  },
}
